import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { ArrowRight, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u?.roleBase === "Sales" ? "/sales" : "/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0b0d10] px-4 py-10">
      {/* Cinematic background */}
      <img
        src="/login-lux-bg.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover animate-lux-fade-in"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0b0d10]/80 via-[#0b0d10]/70 to-[#0b0d10]/90" />
      {/* Ambient brand glows */}
      <div className="pointer-events-none absolute -top-40 -right-32 h-96 w-96 rounded-full bg-[#1C64B0]/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#E23B2E]/15 blur-[120px]" />

      {/* Glass login card */}
      <div
        data-testid="login-card"
        className="relative z-10 w-full max-w-md animate-lux-fade-up rounded-[28px] border border-white/60 bg-white/95 p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-2xl sm:p-11"
      >
        <div className="flex flex-col items-center text-center">
          <div className="mb-6 rounded-2xl bg-white px-6 py-4 shadow-sm ring-1 ring-slate-100">
            <img
              src="/clazing-logo.png"
              alt="Clazing Convenience Food"
              className="h-20 w-auto object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
            Welkom terug
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Log in op het Clazing bedrijfsplatform
          </p>
          <div className="mt-5 h-px w-16 bg-gradient-to-r from-transparent via-[#E23B2E] to-transparent" />
        </div>

        {error && (
          <div
            className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
            data-testid="login-error"
          >
            {error}
          </div>
        )}

        <form onSubmit={submit} className="mt-7 space-y-5">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">E-mailadres</Label>
            <Input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@clazing.nl"
              className="mt-1.5 h-11 rounded-xl border-slate-200 bg-slate-50/60 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-0"
              required
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Wachtwoord</Label>
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-medium text-[#1C64B0] hover:underline">Wachtwoord vergeten?</Link>
            </div>
            <PasswordInput
              data-testid="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 h-11 rounded-xl border-slate-200 bg-slate-50/60 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-0"
              toggleTestId="login-password-toggle"
              required
            />
          </div>
          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="group h-12 w-full rounded-xl bg-slate-900 text-[15px] font-semibold text-white shadow-lg shadow-slate-900/20 transition-transform duration-300 ease-out hover:-translate-y-0.5 hover:bg-slate-800"
          >
            {loading ? "Bezig met inloggen..." : (
              <span className="inline-flex items-center gap-2">
                Inloggen
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </span>
            )}
          </Button>
        </form>

        <div className="mt-7 flex items-center justify-center gap-2 text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5 text-[#1C64B0]" />
          Beveiligde omgeving · Clazing Convenience Food B.V.
        </div>
      </div>
    </div>
  );
}
